import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Hook: active prim rate rules (position-based) with helpers.
 * Returns { rules, loading, byPozisyonNorm, isRateRulePozisyon, getRuleFor, reload }.
 * `isRateRulePozisyon(poz)` returns true when the pozisyon matches an ACTIVE rule.
 * `getRuleFor(poz)` returns the rule object (or null).
 */
const _norm = (p) => {
  const s = String(p || "").trim();
  return s
    .replace(/İ/g, "I")
    .replace(/ı/g, "i")
    .toLowerCase()
    .replace(/ğ/g, "g")
    .replace(/ç/g, "c")
    .replace(/ş/g, "s")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o");
};

export function usePrimRateRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get("/prim-rate-rules");
      setRules(Array.isArray(r.data) ? r.data : []);
    } catch {
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byPozisyonNorm = {};
  for (const rule of rules) {
    if (rule.aktif) byPozisyonNorm[_norm(rule.pozisyon)] = rule;
  }

  const getRuleFor = (poz) => byPozisyonNorm[_norm(poz)] || null;
  const isRateRulePozisyon = (poz) => !!getRuleFor(poz);

  // Explicit Call Center detection (works even if DB rule missing — legacy fallback)
  const isCallCenterPozisyon = (poz) => {
    const n = _norm(poz);
    return n === "call center" || n === "cagri merkezi" || n === "callcenter";
  };

  return {
    rules,
    loading,
    byPozisyonNorm,
    getRuleFor,
    isRateRulePozisyon,
    isCallCenterPozisyon,
    reload: load,
  };
}

export const normalizePozisyon = _norm;
